// Pipeline comercial — conversas por etapa do funil (MET-15).
//
// Diferente dos quadros de contato (`crm/api.ts`), aqui NINGUÉM posiciona nada:
// a coluna sai do estado real do atendimento, carimbado no write-path do
// backend. Arrastar um card é reclassificar a conversa, não mover um cartão.
import { api } from '@/lib/api';
import { mapQueueCard, type QueueCardDto, type RawQueueCard } from '@/features/queue';
import { formatBRL } from '@/lib/utils/formatters';

/** Coluna sintética do backend: janela de 24h vencida vence qualquer etapa. */
export const STALE_COLUMN_ID = 'sem-resposta';

export type PipelineColumnKind = 'OPEN' | 'WON' | 'LOST' | 'NEUTRAL' | 'STALE';

export interface PipelineCard extends QueueCardDto {
  stageId: string | null;
  /** Tempo médio de resposta da conversa, em segundos. `null` = ninguém respondeu. */
  respostaMediaSec: number | null;
  /** Dias sem nenhuma atividade (mensagem ou mudança de etapa). */
  paradoHaDias: number;
  /** Passou do prazo da etapa. É só SINAL — nada se move nem se encerra por isto. */
  rotting: boolean;
  customer: {
    /** `false` = nunca comprou. É o que separa o selo "Lead" do selo "Cliente". */
    returning: boolean;
    orderCount: number;
    /** Já formatado em BRL — `null` quando nunca comprou. */
    totalSpent: string | null;
    lastOrderAt: string | null;
  };
  conclusion: {
    reason: string;
    lossReasonLabel: string | null;
    /** Já formatado em BRL. */
    saleValue: string | null;
    closedAt: string | null;
  } | null;
}

export interface PipelineColumn {
  id: string;
  label: string;
  kind: PipelineColumnKind;
  total: number;
  cards: PipelineCard[];
}

export interface PipelineDto {
  columns: PipelineColumn[];
  /** Conversas abertas sem etapa — anteriores ao carimbo automático. */
  unstaged: number;
  days: number;
}

export interface PipelineArgs {
  storeId?: string;
  days?: number;
}

interface RawPipelineCard extends RawQueueCard {
  stageId: string | null;
  respostaMediaSec: number | null;
  paradoHaDias: number;
  rotting: boolean;
  customer: {
    returning: boolean;
    orderCount: number;
    totalSpentCents: number;
    lastOrderAt: string | null;
  };
  conclusion: {
    reason: string;
    lossReasonLabel: string | null;
    saleValueCents: number | null;
    closedAt: string | null;
  } | null;
}

interface RawPipelineResponse {
  columns: Array<{
    id: string;
    label: string;
    kind: PipelineColumnKind;
    total: number;
    cards: RawPipelineCard[];
  }>;
  unstaged: number;
  days: number;
}

function mapCard(raw: RawPipelineCard): PipelineCard {
  return {
    // Reusa o mapper da Fila: é o MESMO card, e uma segunda cópia divergiria.
    ...mapQueueCard(raw),
    stageId: raw.stageId,
    // `?? null` porque o back antigo (deploy em voo) não manda o campo — sem ele
    // o card mostraria "undefined s".
    respostaMediaSec: raw.respostaMediaSec ?? null,
    paradoHaDias: raw.paradoHaDias ?? 0,
    rotting: raw.rotting ?? false,
    customer: {
      returning: raw.customer.returning,
      orderCount: raw.customer.orderCount,
      totalSpent: raw.customer.returning ? formatBRL(raw.customer.totalSpentCents) : null,
      lastOrderAt: raw.customer.lastOrderAt,
    },
    conclusion: raw.conclusion
      ? {
          reason: raw.conclusion.reason,
          lossReasonLabel: raw.conclusion.lossReasonLabel,
          saleValue:
            raw.conclusion.saleValueCents != null
              ? formatBRL(raw.conclusion.saleValueCents)
              : null,
          closedAt: raw.conclusion.closedAt,
        }
      : null,
  };
}

function qs(params: Record<string, string | number | undefined>): string {
  const entries = Object.entries(params).filter(([, v]) => v !== undefined && v !== '');
  if (entries.length === 0) return '';
  return '?' + entries.map(([k, v]) => `${k}=${encodeURIComponent(String(v))}`).join('&');
}

export const pipelineApi = api.injectEndpoints({
  endpoints: (build) => ({
    pipeline: build.query<PipelineDto, PipelineArgs>({
      query: ({ storeId, days }) => `/crm/pipeline${qs({ storeId, days })}`,
      transformResponse: (raw: RawPipelineResponse): PipelineDto => ({
        columns: raw.columns.map((c) => ({ ...c, cards: c.cards.map(mapCard) })),
        unstaged: raw.unstaged,
        days: raw.days,
      }),
      /**
       * Mesma tag da Fila — e é isto que dá REALTIME ao pipeline sem um listener
       * próprio. `attachQueueWsListener` já invalida `{Conversation, QUEUE}` nos
       * 7 eventos de ciclo de vida (created, status.changed, claimed, released,
       * assigned, message.received, window.closed), com throttle de 2s; como os
       * dois endpoints vivem no MESMO `api` base, a invalidação alcança os dois.
       *
       * Um listener paralelo só somaria uma segunda janela de coalescing sobre os
       * mesmos eventos — e um dia divergiria dela.
       */
      providesTags: [{ type: 'Conversation', id: 'QUEUE' }],
    }),
    /** Move a conversa de etapa (arraste entre colunas ABERTAS). */
    setStage: build.mutation<unknown, { conversationId: string; stageId: string; version: number }>(
      {
        query: ({ conversationId, stageId, version }) => ({
          url: `/conversations/${conversationId}/stage`,
          method: 'PATCH',
          // ⚠️ o campo é `funnelStageId`; `stageId` devolve 400.
          body: { funnelStageId: stageId, version },
        }),
        invalidatesTags: (_r, _e, { conversationId }) => [
          { type: 'Conversation', id: 'QUEUE' },
          { type: 'Conversation', id: conversationId },
          'MetricsAggregate',
        ],
      },
    ),
  }),
  overrideExisting: false,
});

export const { usePipelineQuery, useSetStageMutation } = pipelineApi;
